/* eslint-disable lingui/no-unlocalized-strings */
/* eslint-disable no-restricted-globals */
import { ENV_VARS, MODE } from '@env';
import { pick } from 'lodash-es';
import {
  BUILD_VERSION,
  environment,
  IS_CONNECT_CARRIER,
  IS_CONNECT_CUSTOMER,
  IS_ELECTRON,
  IS_LOCAL_DEV,
  IS_LOCAL_OR_CI_CYPRESS,
  tenantDomainArr,
} from '../utils/constants';
import { sendSync } from '../utils/electron';
import { win } from '../utils/win';
import {
  Config,
  configAliases,
  CrossAppConfig,
  crossAppKeys,
  getPRDomainFromStorage,
} from './base';
import { getConnectCarrierConfig, getConnectCustomerConfig } from './connect';
import { buildConfigFromRegistry } from './tenants/buildConfig';

// Used by src/components/Flag.
export { configAliases };

const {
  // this is only used for the validation script, pnpm config:sync:check
  ORIGIN: originFromEnv,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
} = ENV_VARS;

let origin = originFromEnv || '';
if (typeof window !== 'undefined') {
  origin = window.location.origin;
}

export const authLogoutRedirectUri = origin;

export const sentryDsn =
  'https://5a8f866f49f74656a8e011942fc72ab7@o291138.ingest.sentry.io/1723665';
export const mapboxBaseStyle =
  'mapbox://styles/mastery-logistics/ck5o7qc0w01uz1ijy5qwsjgku';
export const mapboxStreetStyle = 'mapbox://styles/mapbox/streets-v12';
export const mapboxSatelliteStyle =
  'mapbox://styles/mapbox/satellite-streets-v11';
// Custom `Basic-no-cities-dark` style for the state map in dark mode.
export const mapboxBaseDarkStyle =
  'mapbox://styles/mastery-logistics/cmovohsg3001a01safss9egrp';
// Custom `Streets Dark` style for the default map in dark mode.
export const mapboxStreetDarkStyle =
  'mapbox://styles/mastery-logistics/cmovi668k000901qtee2ee8nv';

/* istanbul ignore next */
export const getKnownDomainFromInput = (
  rawStr: string | undefined,
): string | undefined => {
  if (!rawStr) {
    return;
  }
  let str = rawStr;
  try {
    str = new URL(str).hostname;
  } catch {
    // noop
  }
  if (IS_LOCAL_DEV && str === 'localhost') {
    return str;
  }
  return tenantDomainArr.find((d) => d === str) ?? str;
};

let { hostname } = win.location;
hostname = getPRDomainFromStorage() || hostname;

let ELECTRON_DOMAIN: undefined | string = sendSync('store.get', 'space');
if ((ELECTRON_DOMAIN || '').match('localhost')) {
  ELECTRON_DOMAIN = undefined;
}
// This only happens when there is no space selected, and the user is presented with selection screen. We are only satisfying the rawConfig requirement, so it does not throw error.
if (IS_ELECTRON && !ELECTRON_DOMAIN && hostname === '-') {
  ELECTRON_DOMAIN = 'dev.mm100.mastermindtms.com';
}

let RESOLVED_TENANT_HOSTNAME_VAL = ELECTRON_DOMAIN || hostname || '';
if (environment === 'development' && ENV_VARS.VITE_EMULATE_DOMAIN) {
  RESOLVED_TENANT_HOSTNAME_VAL = ENV_VARS.VITE_EMULATE_DOMAIN;
}
if (
  environment === 'development' &&
  IS_CONNECT_CARRIER &&
  ENV_VARS.VITE_CONNECT_CARRIER_EMULATE_DOMAIN
) {
  RESOLVED_TENANT_HOSTNAME_VAL = ENV_VARS.VITE_CONNECT_CARRIER_EMULATE_DOMAIN;
}

if (
  MODE === 'test' ||
  (ENV_VARS as fixMe).START_DEV ||
  typeof window === 'undefined'
) {
  RESOLVED_TENANT_HOSTNAME_VAL = 'localhost';
}
const IS_MERCATOR = MODE === 'development' && ENV_VARS.BASE_URL === '/lib/';

if (IS_MERCATOR || RESOLVED_TENANT_HOSTNAME_VAL === '127.0.0.1') {
  RESOLVED_TENANT_HOSTNAME_VAL = 'localhost';
}
export const RESOLVED_TENANT_HOSTNAME = RESOLVED_TENANT_HOSTNAME_VAL;

// The runtime `Config` is built from the tenant registry (single source of
// truth: src/config/tenants/tenants.config.ts) via `buildConfigFromRegistry`.
const rawConfig: Config | undefined = buildConfigFromRegistry(
  RESOLVED_TENANT_HOSTNAME,
);

export const BASE_TENANT_HOSTNAME = rawConfig?.keycloakRealm;

if (!rawConfig) {
  throw new Error(
    `No config found for ${environment} with domain ${RESOLVED_TENANT_HOSTNAME}`,
  );
}

// Local-dev convenience: when a contributor runs a mastermind-ai backend
// locally, VITE_AI_ENDPOINT points Leo's chat at it (e.g. http://localhost:5566)
// while every other service still targets the emulated remote tenant. Without
// this, VITE_AI_ENDPOINT is silently ignored whenever VITE_EMULATE_DOMAIN is
// set. Mutating rawConfig before the export also keeps endpoints.ai (used by
// fetchHttp's endpoint allow-list) in sync with the transport URL, so multipart
// uploads to a localhost AI url aren't rejected. Gated to local dev (never
// deployed builds) and skipped under Cypress, which drives its own AI mock.
if (IS_LOCAL_DEV && !IS_LOCAL_OR_CI_CYPRESS && ENV_VARS.VITE_AI_ENDPOINT) {
  rawConfig.aiEndpoint = ENV_VARS.VITE_AI_ENDPOINT;
}

export const config: Config = rawConfig;
const generateCrossAppConfigFromConfig = (config: Config): CrossAppConfig =>
  pick(config, crossAppKeys);
/** Cross-app config is a config object with only the data that matters for booting a general Mastery frontend app, such as keycloak information. */
let rawCrossAppConfig: CrossAppConfig =
  generateCrossAppConfigFromConfig(config);

if (IS_CONNECT_CARRIER) {
  rawCrossAppConfig = getConnectCarrierConfig(RESOLVED_TENANT_HOSTNAME);
}

if (IS_CONNECT_CUSTOMER) {
  rawCrossAppConfig = getConnectCustomerConfig(RESOLVED_TENANT_HOSTNAME);
}

export const crossAppConfig = rawCrossAppConfig;
export const connectCarrierConfig = getConnectCarrierConfig(
  RESOLVED_TENANT_HOSTNAME,
);
export const connectCustomerConfig = getConnectCustomerConfig(
  RESOLVED_TENANT_HOSTNAME,
);

try {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ((window || {}) as any).masteryConfig = { ...config, BUILD_VERSION };
} catch {
  // noop
}

if (IS_ELECTRON && !config && sendSync('store.get', 'space')) {
  sendSync('store.delete', 'space');
  win.location.reload();
}
